'use client';

import React, { memo } from 'react';

import { isHackerNewsArticle } from '@/lib/is-hacker-news-article';
import { ArticleAnalysisContent } from '@/components/article-analysis/article-analysis-content';
import { useArticleAnalysisQuery } from '@/components/article-analysis/article-analysis-query';
import { PureArticlesCarousel } from '@/app/(app)/articles/_components/articles-carousel/articles-carousel';
import { ArticlesSectionSkeleton } from '@/app/(app)/articles/_components/skeletons/articles-section-skeleton';
import { ChartsSectionSkeleton } from '@/app/(app)/articles/_components/skeletons/charts-section-skeleton';
import { KeySourcesSectionSkeleton } from '@/app/(app)/articles/_components/skeletons/key-sources-section-skeleton';
import { KeyTakeawaysSectionSkeleton } from '@/app/(app)/articles/_components/skeletons/key-takeaways-section-skeleton';
import { TabsSectionSkeleton } from '@/app/(app)/articles/_components/skeletons/tabs-section-skeleton';

import { ArticleCardAnalysisHeader } from './article-card-analysis-header';

interface ArticleCardAnalysisContentProps {
  /** Fetch key. Null only when the article has no slug (a by-url landing),
   *  which falls back to `articleId`. */
  slug: string | null;
  articleId?: number;
  onClose: () => void;
}

const PureArticleCardAnalysisContent: React.FC<
  ArticleCardAnalysisContentProps
> = ({ slug, articleId, onClose }) => {
  const { data: articleMetrics, isLoading } = useArticleAnalysisQuery(
    slug,
    articleId,
  );

  if (isLoading) {
    return (
      <div className="dark flex size-full flex-1 flex-col space-y-3 bg-black text-[#F0F0F0] @cred-md:space-y-3.5 @cred-xl:space-y-5">
        <ArticlesSectionSkeleton />
        <KeyTakeawaysSectionSkeleton />
        <ChartsSectionSkeleton />
        <TabsSectionSkeleton />
        <KeySourcesSectionSkeleton />
        <div className="h-5"></div>
      </div>
    );
  }

  if (!articleMetrics) {
    return (
      <div className="dark flex size-full flex-1 flex-col space-y-3 bg-black text-[#F0F0F0] @cred-md:space-y-3.5 @cred-xl:space-y-5">
        <div className="flex w-full items-center justify-center py-5">
          <div className="flex flex-col items-center gap-2">
            <span className="font-semibold @cred-md:text-lg">
              Article not found
            </span>
            <p className="text-xs @cred-md:text-sm">
              The requested article could not be loaded
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dark space-y-reverse @cred-md:space-y-reverse @cred-lg:space-y-reverse @cred-xl:space-y-reverse space-y-3 bg-black text-[#F0F0F0] @cred-md:space-y-3.5 @cred-lg:space-y-4 @cred-xl:space-y-5">
      <div className="mt-0! relative min-h-26 w-full bg-cta/30 py-2 @cred-md:min-h-[155px] @cred-md:py-[9.33px] @cred-lg:min-h-[167px] @cred-lg:py-2.5 @cred-xl:min-h-[220px] @cred-xl:py-[13.16px]">
        <ArticleCardAnalysisHeader onClose={onClose} />
        <PureArticlesCarousel
          articleMetrics={articleMetrics}
          isHackerNews={isHackerNewsArticle(articleMetrics)}
        />
      </div>

      <ArticleAnalysisContent articleMetrics={articleMetrics} />

      <div className="h-9.25 @cred-md:h-10 @cred-lg:h-11 @cred-xl:h-12"></div>
    </div>
  );
};
export const ArticleCardAnalysisContent = memo(PureArticleCardAnalysisContent);
